import { EventEmitter } from 'eventemitter3';
import { MetricsManager } from '../MetricsManager';
import * as audioPlayerProcessor from './multiaudioplayer.worklet';
export class AudioPlayer extends EventEmitter {
    audioWorkletPromise;
    audioContext;
    getOptimalDelayCallback;
    mainGainNode;
    trackNodes = new Map();
    isPlaying = false;
    constructor(audioContext, getOptimalDelayCallback) {
        super();
        this.audioContext = audioContext;
        if (audioContext.state === 'running') {
            this.isPlaying = true;
        }
        audioContext.addEventListener('statechange', () => {
            if (audioContext.state === 'running') {
                this.isPlaying = true;
            }
            if (audioContext.state === 'suspended') {
                this.isPlaying = false;
            }
        });
        this.getOptimalDelayCallback = getOptimalDelayCallback;
        this.mainGainNode = this.audioContext.createGain();
        this.mainGainNode.connect(this.audioContext.destination);
    }
    /**
     * Feed a decoded audio packet. Creates a new worklet node when trackId changes.
     */
    feedAudioPacket(packet) {
        let trackNode = this.trackNodes.get(packet.trackId);
        if (!trackNode) {
            trackNode = this.createNewWorkletNode(packet.trackId);
            this.trackNodes.set(packet.trackId, trackNode);
            this.emit('trackAdded', packet.trackId);
        }
        // Get current optimal delay (may be undefined if not yet calculated)
        const optimalDelayMillis = this.getOptimalDelayCallback(packet.trackId);
        // Start playback when optimal delay becomes available
        if (this.isPlaying &&
            !trackNode.isPlaying &&
            optimalDelayMillis !== undefined) {
            console.log('starting audio playback at optimal delay', optimalDelayMillis, packet.trackId);
            trackNode.isPlaying = true;
        }
        // Feed audio frame with current optimal delay piggybacked
        trackNode.workletNode.port.postMessage({
            type: 'feedAudioFrame',
            decodedAudioData: packet.audioData,
            optimalDelayMillis, // Piggyback optimal delay with every frame
            startPlayback: trackNode.isPlaying,
        }, packet.audioData.channels.map((channel) => channel.buffer));
        /**
         * @todo Fix `endOfTrack` handling. We `postMessage` audio data but then `removeTrack`
         * immediately, disconnecting the worklet before it can play. Since `postMessage` is
         * async, `endOfTrack` audio may be buffered but never heard.
         */
        if (packet.endOfTrack) {
            this.removeTrack(packet.trackId);
        }
    }
    async init() {
        if (!this.audioWorkletPromise) {
            this.audioWorkletPromise = this.audioContext.audioWorklet.addModule(
            // biome-ignore lint/suspicious/noExplicitAny: @todo fix types (the "any")
            audioPlayerProcessor);
        }
        await this.audioWorkletPromise;
    }
    // This will start audio playback `optimalDelayMillis` milliseconds
    // from the end of the audio buffer, if there is enough data
    // in the buffer. If there is not enough data, it will start
    // buffering the audio and then start playback when enough data
    // is available.
    /*
    public startAtOptimalDelayMillis(optimalDelayMillis: number): void {
        if (this.optimalDelayMillis === optimalDelayMillis) {
            return;
        }

        this.optimalDelayMillis = optimalDelayMillis;

        for (const trackNode of this.trackNodes.values()) {
            trackNode.workletNode.port.postMessage({
                type: 'startAtOptimalDelayMillis',
                optimalDelayMillis,
            });
        }
    }
    */
    async reset() {
        const tasks = [];
        for (const trackNode of this.trackNodes.values()) {
            const promise = new Promise((resolve) => {
                const handler = (e) => {
                    if (e.data.type === 'resetCompleted') {
                        trackNode.workletNode.port.removeEventListener('message', handler);
                        resolve();
                    }
                };
                trackNode.workletNode.port.addEventListener('message', handler);
                trackNode.workletNode.port.postMessage({ type: 'reset' });
            });
            tasks.push(promise);
        }
        await Promise.all(tasks);
        this.emit('workletResetCompleted');
    }
    async dispose() {
        for (const trackId of this.trackNodes.keys()) {
            this.removeTrack(trackId);
        }
        await this.audioContext.close();
    }
    /**
     * Creates a new audio worklet node and gain node for a specific track.
     *
     * This method assumes that `init()` has been called and awaited before invocation,
     * ensuring the worklet module is properly loaded and the AudioContext is initialized.
     */
    createNewWorkletNode(trackId) {
        const workletNode = new AudioWorkletNode(this.audioContext, 'audio-player');
        const gainNode = this.audioContext.createGain();
        workletNode.connect(gainNode);
        gainNode.connect(this.mainGainNode);
        workletNode.port.onmessage = (e) => {
            if (e.data.type === 'purged') {
                // The 'purged' message should not be removed even though it is not currently used.
                // It is used for moving the AudioData from the worklet to the main thread, where it can be garbage-collected.
            }
            if (e.data.type === 'played') {
                const presentationTimeMicros = Number(e.data.frame.timestamp) +
                    Number(e.data.frame.duration);
                const trackNode = this.trackNodes.get(trackId);
                if (trackNode) {
                    //if (trackNode.referenceTimeCounter % 10 === 0) {
                    this.emit('newReferenceTimes', trackId, presentationTimeMicros, e.data.realTimeMillis);
                    //}
                    //trackNode.referenceTimeCounter++;
                }
            }
            if (e.data.type === 'bufferMetrics') {
                // Forward worklet logs to main thread console so Playwright can capture them
                //console.log(
                //    `audioBufferLengthMilliseconds ${e.data.bufferLengthMs}`,
                //);
                // Log to metrics service
                MetricsManager.getInstance().logMetric('audioBufferLengthMillisecondsTrack ' + trackId, e.data.bufferLengthMs);
            }
            if (e.data.type === 'debug') {
                // Forward debug messages from worklet
                //console.log(`[AudioWorklet] ${e.data.message}`);
            }
            if (e.data.type === 'resetCompleted') {
                // Per-track reset completion is handled in reset() method
            }
        };
        return {
            workletNode,
            gainNode,
            trackId,
            isPlaying: false,
            //referenceTimeCounter: 0,
        };
    }
    removeTrack(trackId) {
        const trackNode = this.trackNodes.get(trackId);
        if (!trackNode) {
            return;
        }
        trackNode.workletNode.port.onmessage = null;
        try {
            trackNode.workletNode.disconnect();
            trackNode.gainNode.disconnect();
        }
        catch (_) {
            // ignore
        }
        this.trackNodes.delete(trackId);
        this.emit('trackRemoved', trackId);
    }
    getActiveTrackIds() {
        return [...this.trackNodes.keys()];
    }
    /**
     * Set the gain for a specific track (0.0 to 1.0+).
     */
    setTrackGain(trackId, gain) {
        const trackNode = this.trackNodes.get(trackId);
        if (trackNode && trackNode.gainNode.gain.value !== gain) {
            trackNode.gainNode.gain.value = gain;
            this.emit('trackGainChanged', trackId, gain);
        }
    }
    /**
     * Set the main output gain (0.0 to 1.0+).
     */
    setMainGain(gain) {
        if (this.mainGainNode.gain.value !== gain) {
            this.mainGainNode.gain.value = gain;
            this.emit('mainGainChanged', gain);
        }
    }
}
